import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import { Slider } from "@/components/ui/slider";
import { ArrowUpRight, Zap, Wallet, BadgeIndianRupee, PiggyBank, Timer, TrendingUp } from "lucide-react";
import Counter from "@/components/Counter";

function calc(bill) {
  const units = bill / 7;
  const kw = Math.min(25, Math.max(1, Math.ceil(units / 120)));
  const rate = kw <= 3 ? 60000 : kw <= 10 ? 55000 : 50000;
  const investment = kw * rate;
  const subsidy = kw >= 3 ? 78000 : kw * 30000;
  const netCost = Math.max(0, investment - subsidy);
  const monthlySavings = Math.round(bill * 0.9);
  const lifetime = monthlySavings * 12 * 25;
  const payback = netCost / (monthlySavings * 12);
  return { kw, investment, subsidy, netCost, monthlySavings, lifetime, payback };
}

export default function Calculator() {
  const [bill, setBill] = useState(3000);
  const r = useMemo(() => calc(bill), [bill]);

  const chartData = useMemo(() => {
    const annual = r.monthlySavings * 12;
    return Array.from({ length: 25 }, (_, i) => ({
      year: `Y${i + 1}`,
      savings: annual * (i + 1) - (i === 0 ? r.netCost : i === 0 ? 0 : 0),
      cumulative: annual * (i + 1) - r.netCost,
    }));
  }, [r]);

  const outputs = [
    { icon: Zap, label: "Recommended Capacity", value: r.kw, suffix: " kW", testid: "calc-output-kw" },
    { icon: Wallet, label: "System Investment", value: r.investment, prefix: "₹", testid: "calc-output-investment" },
    { icon: BadgeIndianRupee, label: "PM Surya Ghar Subsidy", value: r.subsidy, prefix: "₹", testid: "calc-output-subsidy", gold: true },
    { icon: PiggyBank, label: "Net Cost After Subsidy", value: r.netCost, prefix: "₹", testid: "calc-output-net" },
    { icon: TrendingUp, label: "Monthly Bill Savings", value: r.monthlySavings, prefix: "₹", testid: "calc-output-monthly" },
    { icon: Timer, label: "Payback Period", value: Math.round(r.payback * 10) / 10, suffix: " yrs", testid: "calc-output-payback", decimal: true },
  ];

  return (
    <div data-testid="calculator-page">
      <section className="relative bg-brand-deep text-white grain overflow-hidden pt-32 pb-20">
        <div className="absolute -top-32 -right-32 w-[26rem] h-[26rem] rounded-full bg-gold/10 blur-3xl" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 relative z-10">
          <motion.p
            initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-mono text-xs tracking-[0.35em] text-gold mb-4"
          >
            SOLAR CALCULATOR
          </motion.p>
          <div className="overflow-hidden">
            <motion.h1
              initial={{ y: "100%" }} animate={{ y: 0 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]"
            >
              Slide your bill. <span className="text-gold">See your sun.</span>
            </motion.h1>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.7 }}
            className="mt-6 max-w-2xl text-white/70 text-base md:text-lg"
          >
            One slider. Real-time system size, subsidy and 25-year savings — computed instantly.
          </motion.p>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 py-16">
        <div className="grid lg:grid-cols-5 gap-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="lg:col-span-2 rounded-[2rem] bg-white border border-brand/10 p-8 md:p-10 shadow-[0_16px_50px_rgba(3,59,43,0.08)]"
          >
            <p className="font-mono text-xs tracking-[0.3em] text-brand">YOUR MONTHLY BILL</p>
            <p className="mt-3 font-display font-extrabold text-5xl md:text-6xl text-ink" data-testid="calc-bill-display">
              ₹{bill.toLocaleString("en-IN")}
            </p>
            <div className="mt-10">
              <Slider
                data-testid="calculator-bill-slider"
                value={[bill]}
                onValueChange={([v]) => setBill(v)}
                min={500}
                max={50000}
                step={250}
                aria-label="Monthly electricity bill"
              />
              <div className="mt-3 flex justify-between font-mono text-xs text-moss">
                <span>₹500</span><span>₹50,000</span>
              </div>
            </div>

            <div className="mt-10 rounded-2xl bg-gold-soft border border-gold/40 p-5">
              <p className="text-sm font-semibold text-brand-deep leading-relaxed">
                A <span className="font-display font-extrabold text-2xl text-brand">{r.kw} kW</span> rooftop
                plant wipes out up to 90% of this bill — and the government pays ₹{r.subsidy.toLocaleString("en-IN")} of the cost.
              </p>
            </div>

            <Link
              to={`/contact?bill=${bill}&kw=${r.kw}`}
              data-testid="calc-lead-cta"
              className="mt-8 flex items-center justify-center gap-2 rounded-full bg-brand text-white py-4 font-bold hover:scale-[1.02] transition-transform"
            >
              Get Exact On-Site Quote <ArrowUpRight size={18} />
            </Link>
            <p className="mt-3 text-center text-xs text-moss">Free design + survey. No obligation.</p>
          </motion.div>

          <div className="lg:col-span-3 space-y-8">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {outputs.map((o, i) => (
                <motion.div
                  key={o.label}
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 + i * 0.06, duration: 0.6 }}
                  data-testid={o.testid}
                  className={`rounded-3xl p-5 border ${
                    o.gold ? "bg-gold border-gold" : "bg-white border-brand/10"
                  }`}
                >
                  <o.icon size={20} className={o.gold ? "text-brand-deep" : "text-brand"} />
                  <p className={`mt-3 font-display font-extrabold text-2xl md:text-[1.7rem] leading-none ${o.gold ? "text-brand-deep" : "text-ink"}`}>
                    {o.decimal ? (
                      <span>{o.prefix || ""}{o.value}{o.suffix || ""}</span>
                    ) : (
                      <Counter value={o.value} prefix={o.prefix || ""} suffix={o.suffix || ""} duration={0.5} />
                    )}
                  </p>
                  <p className={`mt-2 text-[11px] font-semibold tracking-wide uppercase ${o.gold ? "text-brand-deep/70" : "text-moss"}`}>
                    {o.label}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.div
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.35, duration: 0.7 }}
              className="rounded-[2rem] bg-brand-deep text-white p-6 md:p-8 grain relative overflow-hidden"
              data-testid="calc-savings-chart"
            >
              <div className="relative z-10">
                <div className="flex flex-wrap items-end justify-between gap-3">
                  <div>
                    <p className="font-mono text-[10px] tracking-[0.3em] text-gold">25-YEAR CUMULATIVE SAVINGS</p>
                    <p className="mt-1 font-display font-extrabold text-3xl md:text-4xl text-gold">
                      ₹<Counter value={r.lifetime} duration={0.5} />
                    </p>
                  </div>
                  <p className="text-xs text-white/50 max-w-[16rem]">After recovering full system cost. Assumes current tariff — actual savings grow as tariffs rise.</p>
                </div>
                <div className="mt-6 h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chartData} margin={{ top: 5, right: 5, left: 5, bottom: 0 }}>
                      <defs>
                        <linearGradient id="goldFill" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#e7c90d" stopOpacity={0.55} />
                          <stop offset="100%" stopColor="#e7c90d" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <XAxis dataKey="year" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} axisLine={false} tickLine={false} interval={4} />
                      <YAxis hide />
                      <Tooltip
                        contentStyle={{ background: "#022a1f", border: "1px solid rgba(231,201,13,0.3)", borderRadius: 12, fontSize: 12 }}
                        labelStyle={{ color: "#e7c90d" }}
                        formatter={(v) => [`₹${Math.round(v).toLocaleString("en-IN")}`, "Net savings"]}
                      />
                      <Area type="monotone" dataKey="cumulative" stroke="#e7c90d" strokeWidth={2.5} fill="url(#goldFill)" />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        <p className="mt-8 text-xs text-moss max-w-3xl">
          Estimates assume ₹7/unit average tariff, 120 units/month per kW generation in Karnataka conditions, and PM Surya Ghar subsidy norms (₹30,000/kW up to 2 kW, ₹78,000 for 3 kW+). Exact figures confirmed after a free on-site survey.
        </p>
      </section>
    </div>
  );
}
